import { Controller } from '@hotwired/stimulus'
import { loadTurboFrame } from 'helpers/turbo_frames'

// Drives the moment-survey form: Q1 reveals the activity picker, the picker frame
// loads the code roots matching the chosen answer (Yes -> working, No ->
// not_working), and Q3 appears once a code is picked, following its root.
export default class extends Controller {
  static targets = [
    'sections',
    'frame',
    'codeId',
    'codeSelections',
    'codeError',
    'optionCards',
    'q1Error',
    'description',
    'descriptionSection',
    'descriptionError',
    'questionThreePrompt',
    'questionThreeOptions',
    'optionsError',
    'optionDetailError',
    'interactive',
    'loading'
  ]
  static values = { workingUrl: String, notWorkingUrl: String }

  connect() {
    // The card ships inert behind a spinner so a click cannot land on a form that is not
    // wired up yet — see the comment in _survey_card.html.haml. Dropping --booting also
    // cancels the rest of that CSS timeline, whose next step replaces the form with a
    // "could not load" banner. Guarded because a Turbo cache restore reconnects a card
    // this already ran on.
    this.element.classList.remove('ts-survey-card--booting')
    this.interactiveTargets.forEach(section => section.removeAttribute('inert'))
    if (this.hasLoadingTarget) this.loadingTarget.remove()

    // On a 422 re-render the server already rendered the right picker; seeding
    // currentType from the checked radio prevents a src-set from wiping it.
    const checked = this.element.querySelector('input[name="time_study_response[working_status]"]:checked')
    this.currentType = checked ? checked.dataset.timeStudySurveyFormActivityTypeParam : null
    // Likewise for Q3: the server already revealed the block for this root.
    this.questionThreeRootId = this.pickedRootId()
    // An answer given before this controller connected — the pack was still loading —
    // fired its input event at nobody, and re-clicking the same radio fires no second
    // one, so Q2 would stay hidden and unloaded for good. A server-rendered answer
    // always arrives with the section already open, so a closed one means exactly this.
    if (checked && this.sectionsTarget.classList.contains('d-none')) this.loadActivityCodes()
  }

  select(event) {
    this.sectionsTarget.classList.remove('d-none')

    const type = event.params.activityType
    if (type === this.currentType) return

    this.currentType = type
    this.loadActivityCodes()
  }

  // Points the picker frame at the code roots for the current answer.
  loadActivityCodes() {
    this.sectionsTarget.classList.remove('d-none')
    this.toggleDescriptionSection()
    // This frame reload is a working-status switch, not the user drilling the
    // picker. Its turbo:frame-load must not wipe a code error that previewSubmit
    // may show in the same tick — on slow loads the events race (see spec).
    this.switchingType = true
    loadTurboFrame(this.frameTarget, this.currentType === 'working' ? this.workingUrlValue : this.notWorkingUrlValue)
  }

  // Q2 drill-down. The rows carry only their drilled path; the base urls (host prefix
  // plus survey) come from the card, so a row never has to be a navigable link — see
  // the comment in _activity_code_picker.html.haml.
  drill(event) {
    const base = event.params.activityType === 'not_working' ? this.notWorkingUrlValue : this.workingUrlValue
    const url = new URL(base, window.location.origin)
    // Stimulus JSON-parses params, so a lone selection arrives as a number.
    url.searchParams.set('selections', String(event.params.selections ?? ''))
    loadTurboFrame(this.frameTarget, url)
  }

  // Q3's prompt and options are configured on the picked code's root, so it stays
  // hidden until Q2 is answered. We also never ask what a participant was up to
  // while off the clock (GRC-528).
  toggleDescriptionSection() {
    const hidden = this.currentType === 'not_working' || !this.pickedCodeId()
    this.descriptionSectionTarget.classList.toggle('d-none', hidden)
    if (hidden) {
      this.clearDescriptionError()
      this.clearOptionErrors()
    }
  }

  clearCodeError() {
    if (this.switchingType) {
      this.switchingType = false
      return
    }
    if (this.hasCodeErrorTarget) this.codeErrorTarget.classList.add('d-none')
  }

  clearDescriptionError() {
    if (this.hasDescriptionErrorTarget) this.descriptionErrorTarget.classList.add('d-none')
  }

  clearOptionErrors() {
    if (this.hasOptionsErrorTarget) this.optionsErrorTarget.classList.add('d-none')
    if (this.hasOptionDetailErrorTarget) this.optionDetailErrorTarget.classList.add('d-none')
  }

  // Set only once the drill reaches a leaf — i.e. Q2 is actually answered. Both of these
  // live inside the picker frame, so the guards are load-bearing: connect() runs before
  // any picker exists, and a frame that rendered an error body has neither field.
  pickedCodeId() {
    return this.hasCodeIdTarget ? this.codeIdTarget.value : ''
  }

  // The root code is the first id in the picker's drilled path.
  pickedRootId() {
    if (!this.hasCodeSelectionsTarget) return ''
    return this.codeSelectionsTarget.value.split(',')[0] || ''
  }

  clearWorkingStatusError() {
    this.optionCardsTarget.classList.remove('ts-option-cards--error')
    if (this.hasQ1ErrorTarget) this.q1ErrorTarget.classList.add('d-none')
  }

  // Q3's prompt and inputs are configured on the root code, but Q3 lives outside
  // the picker frame — so reveal the blocks matching whichever root is picked.
  // Inputs in the hidden blocks are disabled so they can never post.
  syncQuestionThree() {
    // Ahead of the early return: drilling deeper keeps the root but can complete the pick.
    this.toggleDescriptionSection()

    const rootId = this.pickedRootId()
    if (rootId === this.questionThreeRootId) return
    this.questionThreeRootId = rootId

    const prompt =
      this.questionThreePromptTargets.find(el => el.dataset.rootCodeId === rootId) ||
      this.questionThreePromptTargets.find(el => !el.dataset.rootCodeId)
    this.questionThreePromptTargets.forEach(el => el.classList.toggle('d-none', el !== prompt))

    const options = this.questionThreeOptionsTargets.find(el => el.dataset.rootCodeId === rootId)
    this.questionThreeOptionsTargets.forEach(el => {
      const active = el === options
      el.classList.toggle('d-none', !active)
      el.querySelectorAll('input[type="checkbox"]').forEach(box => {
        box.disabled = !active
      })
      el.querySelectorAll('[data-option-detail]').forEach(field => this.resetOptionDetail(field, active))
    })

    this.clearDescriptionError()
    this.clearOptionErrors()
  }

  // A free-text detail only applies while its own option is checked.
  toggleOptionDetail(event) {
    const field = event.target.closest('.ts-survey-option-row')?.querySelector('[data-option-detail]')
    if (field) this.applyOptionDetailState(field, event.target.checked)
    this.clearOptionErrors()
  }

  resetOptionDetail(field, blockActive) {
    const checkbox = field.closest('.ts-survey-option-row')?.querySelector('input[type="checkbox"]')
    this.applyOptionDetailState(field, Boolean(blockActive && checkbox?.checked))
  }

  applyOptionDetailState(field, shown) {
    field.classList.toggle('d-none', !shown)
    field.disabled = !shown
    if (!shown) field.value = ''
  }

  // Preview mode: the form never posts, so mimic the server-side validations
  // and show the same inline errors before revealing the thanks banner.
  previewSubmit() {
    if (!this.element.querySelector('input[name="time_study_response[working_status]"]:checked')) {
      // Q2/Q3 are still hidden at this point, so theirs are the only errors worth raising later.
      this.optionCardsTarget.classList.add('ts-option-cards--error')
      this.q1ErrorTarget.classList.remove('d-none')
      return
    }

    if (!this.pickedCodeId()) {
      // Q3 is still hidden without a code, so its errors are not worth raising.
      this.codeErrorTarget.classList.remove('d-none')
      return
    }

    if (this.currentType !== 'not_working' && !this.questionThreeAnswered()) return

    this.element.closest('form').classList.add('d-none')
    // Outside the form, so outside this controller — the whole form is what it replaces.
    document.getElementById('time_study_preview_thanks').classList.remove('d-none')
  }

  // Mirrors Response's Q3 validations: the narrative is always required, and a root
  // offering options also needs one checked plus free text on any that requires it.
  // Every failing check is raised at once rather than one per submit.
  questionThreeAnswered() {
    let answered = true

    if (!this.descriptionTarget.value.trim()) {
      this.descriptionErrorTarget.classList.remove('d-none')
      answered = false
    }

    const options = this.questionThreeOptionsTargets.find(el => !el.classList.contains('d-none'))
    if (!options) return answered

    const checked = [...options.querySelectorAll('input[type="checkbox"]')].filter(box => box.checked)
    if (checked.length === 0) {
      this.optionsErrorTarget.classList.remove('d-none')
      return false
    }

    const missingDetail = checked.some(box => {
      const field = box.closest('.ts-survey-option-row')?.querySelector('[data-option-detail]')
      return field && !field.value.trim()
    })
    if (missingDetail) {
      this.optionDetailErrorTarget.classList.remove('d-none')
      answered = false
    }

    return answered
  }
}
